/* assets/single-flower-poster-renderer.js
   Single Flower Poster renderer:
   - Preview: canvas (fast)
   - Export: JPG preview + PDF @ 300 DPI, exact mm size
   - Layers:
       1) Full poster background (WHITE)
       2) Colored RECT (artboard) at the top
       3) Flower inside colored rect (centered, user-scaled, NEVER squished)
       4) Texture overlay (multiply blend, optional)
       5) Name + Year on the WHITE area UNDER the rect

   Flower scaling: percentage slider (50-150), no cm-based model.
   Flower always centered in artboard.
   Left-right flip supported.
*/

(function (root, factory) {
  if (typeof module === "object" && module.exports) module.exports = factory();
  else root.SingleFlowerPosterRenderer = factory();
})(typeof self !== "undefined" ? self : this, function () {
  // "Design space" (logical coordinates)
  const DESIGN_W = 900;
  const DESIGN_H = 1200;

  // Print sizes in mm
  const SIZE_MM = {
    "A4":    { w: 210, h: 297 },
    "21x30": { w: 210, h: 300 },
    "30x40": { w: 300, h: 400 },
    "40x50": { w: 400, h: 500 },
    "50x70": { w: 500, h: 700 },
  };

  // ============================================================
  // FLOWER REGISTRY
  // ============================================================
  const FLOWER_ASSETS = {};
  let DEFAULT_FLOWER_KEY = null;

  function upsertFlower(key, { assetUrl, label } = {}) {
    const k = String(key || "");
    if (!k) return;
    FLOWER_ASSETS[k] = FLOWER_ASSETS[k] || { key: k, assetUrl: "", label: k };
    if (typeof assetUrl === "string") FLOWER_ASSETS[k].assetUrl = assetUrl;
    if (typeof label === "string") FLOWER_ASSETS[k].label = label;
    if (!DEFAULT_FLOWER_KEY) DEFAULT_FLOWER_KEY = k;
  }

  function setDefaultFlowerKey(key) {
    DEFAULT_FLOWER_KEY = String(key || "") || DEFAULT_FLOWER_KEY;
  }

  function getFlower(key) {
    if (key && FLOWER_ASSETS[key]) return FLOWER_ASSETS[key];
    if (DEFAULT_FLOWER_KEY && FLOWER_ASSETS[DEFAULT_FLOWER_KEY])
      return FLOWER_ASSETS[DEFAULT_FLOWER_KEY];
    return null;
  }

  // ============================================================
  // DEFAULT STYLE
  // ============================================================
  const DEFAULT_STYLE = {
    // Colors
    rectColor: "#EDEBE7",
    posterBgColor: "#FFFFFF",
    textColor: "#111111",
    fontFamily: "RockwellPoster",

    // Outer margins for rect
    marginTop: 66,
    marginLeft: 66,
    marginRight: 66,

    // White footer under rect for text
    footerHeight: 114,
    footerGap: 22,
    footerPaddingX: 70,
    footerPaddingBottom: 30,

    // Text styling
    nameSize: 13,
    detailSize: 9,
    letterSpacingName: 8,
    letterSpacingDetail: 7,
    detailSeparator: " \u00B7 ",

    // Text positions inside footer (measured from footer bottom)
    NAME_Y_FROM_FOOTER_BOTTOM: 68,
    DETAIL_Y_FROM_FOOTER_BOTTOM: 45,

    // Flower shadow
    flowerShadow: true,

    // Texture overlay composite operation
    textureComposite: "multiply",

    // Debug
    debug: false,
  };

  // ============================================================
  // GEOMETRY HELPERS
  // ============================================================
  function getArtboardRect(style) {
    const x = style.marginLeft;
    const y = style.marginTop;
    const w = DESIGN_W - style.marginLeft - style.marginRight;
    const h = DESIGN_H - style.marginTop - style.footerGap - style.footerHeight;
    return { x, y, w, h: Math.max(1, h) };
  }

  function getFooterRect(style, artboard) {
    const x = 0;
    const y = artboard.y + artboard.h + style.footerGap;
    const w = DESIGN_W;
    const h = Math.max(1, DESIGN_H - y);
    return { x, y, w, h };
  }

  // ============================================================
  // UTILITY HELPERS
  // ============================================================
  function parseCmFactor(val) {
    if (val == null) return 1;
    const n = parseFloat(String(val).replace(",", "."));
    return isFinite(n) && n > 0 ? n : 1;
  }

  function safeFilename(s) {
    return String(s || "")
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-zA-Z0-9\-_]/g, "");
  }

  function downloadDataUrl(dataUrl, filename) {
    const a = document.createElement("a");
    a.download = filename;
    a.href = dataUrl;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  function mmToPx(mm, dpi) {
    return Math.round((mm / 25.4) * dpi);
  }

  function makeOffscreenCanvas(pxW, pxH) {
    const off = document.createElement("canvas");
    off.width = pxW;
    off.height = pxH;
    const offCtx = off.getContext("2d");

    offCtx.imageSmoothingEnabled = true;
    offCtx.imageSmoothingQuality = "high";

    offCtx.setTransform(pxW / DESIGN_W, 0, 0, pxH / DESIGN_H, 0, 0);
    return { off, offCtx };
  }

  // ============================================================
  // CANVAS SETUP
  // ============================================================
  function setupPreviewCanvas(canvas, ctx, dprMax = 3, sizeKey) {
    const dpr = Math.max(1, Math.min(dprMax, window.devicePixelRatio || 1));
    const rect = canvas.getBoundingClientRect();
    const cssW = Math.max(1, Math.floor(rect.width));

    let ratio = DESIGN_H / DESIGN_W;
    if (sizeKey) {
      const mm = SIZE_MM[sizeKey] || SIZE_MM["50x70"];
      ratio = (Number(mm.h) || 700) / (Number(mm.w) || 500);
    }

    const cssH = Math.max(1, Math.floor(cssW * ratio));
    canvas.style.height = `${cssH}px`;
    canvas.width = Math.floor(cssW * dpr);
    canvas.height = Math.floor(cssH * dpr);

    ctx.setTransform(
      canvas.width / DESIGN_W,
      0,
      0,
      canvas.height / DESIGN_H,
      0,
      0,
    );

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
  }

  function setupPreviewCanvasForSize(canvas, ctx, sizeKey, dprMax = 3) {
    return setupPreviewCanvas(canvas, ctx, dprMax, sizeKey);
  }

  // ============================================================
  // FONT + IMAGE LOADING
  // ============================================================
  async function ensureFontLoaded(style = DEFAULT_STYLE) {
    if (!document.fonts || !document.fonts.load) return;
    await document.fonts.load(
      `400 ${style.nameSize}px ${style.fontFamily}`,
      "ABCDEFGHIJKLMNOPQRSTUVWXYZÅÄÖ",
    );
    await document.fonts.load(
      `400 ${style.detailSize}px ${style.fontFamily}`,
      "0123456789",
    );
    if (document.fonts.ready) await document.fonts.ready;
  }

  async function loadImageTo(imgEl, url) {
    return new Promise((resolve, reject) => {
      imgEl.onload = async () => {
        try {
          if (imgEl.decode) await imgEl.decode();
        } catch (_) {}
        resolve();
      };
      imgEl.onerror = reject;
      imgEl.crossOrigin = "anonymous";
      imgEl.src = url;
    });
  }

  // ============================================================
  // TEXT DRAWING HELPERS
  // ============================================================
  function drawSpacedText(ctx, text, centerX, y, letterSpacing) {
    const chars = String(text || "").split("");
    let totalWidth = 0;
    for (const ch of chars)
      totalWidth += ctx.measureText(ch).width + letterSpacing;
    totalWidth = Math.max(0, totalWidth - letterSpacing);

    let x = centerX - totalWidth / 2;
    for (const ch of chars) {
      ctx.fillText(ch, x, y);
      x += ctx.measureText(ch).width + letterSpacing;
    }
  }

  function fitTextSize(ctx, text, maxWidth, startPx, minPx, fontFamily) {
    let px = startPx;
    while (px >= minPx) {
      ctx.font = `${px}px ${fontFamily}, RockwellPoster`;
      if (ctx.measureText(text).width <= maxWidth) return px;
      px -= 0.5;
    }
    return minPx;
  }

  // ============================================================
  // FLOWER DRAWING (centered, user-scaled, flip support)
  // ============================================================
  function drawFlowerInsideArtboard(ctx, flowerImg, input, style, artboard) {
    if (!flowerImg || !(flowerImg.naturalWidth || flowerImg.width)) return;

    const iw = flowerImg.naturalWidth || flowerImg.width;
    const ih = flowerImg.naturalHeight || flowerImg.height;

    // User scale as percentage (50-150, default 100)
    const userScale = (Number(input.flowerScale) || 100) / 100;

    // Per-flower cm factor (admin-configured, e.g. 0.75 shrinks base size)
    const cmFactor = parseCmFactor(input.cmFactor);

    // Base scale: fit image to ~80% of artboard (whichever dimension is limiting)
    const baseScale = Math.min(
      (artboard.w * 0.8) / iw,
      (artboard.h * 0.8) / ih,
    );

    const scale = baseScale * cmFactor * userScale;
    let drawW = iw * scale;
    let drawH = ih * scale;

    // Clamp to artboard bounds
    if (drawW > artboard.w) {
      const f = artboard.w / drawW;
      drawW *= f;
      drawH *= f;
    }
    if (drawH > artboard.h) {
      const f = artboard.h / drawH;
      drawW *= f;
      drawH *= f;
    }

    // Center in artboard
    const x = artboard.x + (artboard.w - drawW) / 2;
    const y = artboard.y + (artboard.h - drawH) / 2;

    // Flip if direction is "right" (default is "left")
    const flipX = input.direction === "right";

    ctx.save();

    if (style.flowerShadow) {
      ctx.shadowColor = "rgba(0,0,0,0.10)";
      ctx.shadowBlur = 14;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 8;
    }

    const cx = x + drawW / 2;
    const cy = y + drawH / 2;
    ctx.translate(cx, cy);
    if (flipX) ctx.scale(-1, 1);
    ctx.drawImage(flowerImg, -drawW / 2, -drawH / 2, drawW, drawH);

    ctx.restore();

    if (style.debug) {
      console.log("[Single flower render]", {
        iw,
        ih,
        userScale,
        baseScale,
        scale,
        drawW: Math.round(drawW),
        drawH: Math.round(drawH),
        flipX,
      });
    }
  }

  // ============================================================
  // TEXTURE OVERLAY
  // ============================================================
  // `bleedD` = {x, y} in DESIGN units: how far past the trim edge the canvas
  // extends on a print export. The texture has to cover that strip too, or the
  // 4 mm the printer trims off is bare paper colour and the seam shows if the cut
  // drifts. The tiles grow with the sheet (~1% at 4 mm), so the texture inside
  // the trim is effectively unchanged.
  function drawTextureOverlay(ctx, textureImg, style, bleedD) {
    if (!textureImg || !(textureImg.naturalWidth || textureImg.width)) return;

    ctx.save();
    ctx.globalCompositeOperation = style.textureComposite || "multiply";
    ctx.globalAlpha = style.textureOpacity != null ? style.textureOpacity : 0.4;

    // Draw texture at quarter size (half width, half height), tiled 2×2
    const bx = (bleedD && bleedD.x) || 0;
    const by = (bleedD && bleedD.y) || 0;
    const tileW = (DESIGN_W + bx * 2) / 2;
    const tileH = (DESIGN_H + by * 2) / 2;
    for (let row = 0; row < 2; row++) {
      for (let col = 0; col < 2; col++) {
        ctx.drawImage(textureImg, -bx + col * tileW, -by + row * tileH, tileW, tileH);
      }
    }

    ctx.restore();
  }

  // ============================================================
  // POSTER RENDER
  // ============================================================
  function renderPoster(ctx, flowerImg, input, styleOverride, textureImg) {
    const style = { ...DEFAULT_STYLE, ...(styleOverride || {}) };

    const artboard = getArtboardRect(style);
    const footer = getFooterRect(style, artboard);

    // 1) Clear + WHITE full background. Filled over the ENTIRE physical buffer
    // (identity transform), not just the design rect: a print export renders onto
    // a canvas that is trim + bleed, and the paper colour has to run all the way
    // to the sheet edge or the trimmed-off strip comes out transparent/white.
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.fillStyle = style.posterBgColor || "#FFFFFF";
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.restore();

    // 2) Colored rect (artboard)
    ctx.fillStyle = style.rectColor || "#EDEBE7";
    ctx.fillRect(artboard.x, artboard.y, artboard.w, artboard.h);

    // 3) Flower inside rect (centered, scaled, optionally flipped)
    drawFlowerInsideArtboard(ctx, flowerImg, input, style, artboard);

    // 4) Texture overlay (optional PNG, drawn with multiply/overlay blend).
    // input.bleedDesignPx (number, or {x,y} for a non-uniform scale) is set by the
    // print exporters, which render onto the full sheet; 0/absent on screen.
    if (textureImg) {
      const _b = input && input.bleedDesignPx;
      const bleedD = (typeof _b === "number")
        ? { x: _b, y: _b }
        : { x: (_b && _b.x) || 0, y: (_b && _b.y) || 0 };
      drawTextureOverlay(ctx, textureImg, style, bleedD);
    }

    // 5) Text on WHITE footer
    const centerX = DESIGN_W / 2;
    const maxWidth = DESIGN_W - style.footerPaddingX * 2;

    ctx.fillStyle = style.textColor;
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";

    const footerBottomY = footer.y + footer.h - style.footerPaddingBottom;

    // Line 1: Name · Month · Year
    const nameParts = [
      String(input.name || "").trim(),
      String(input.month || "").trim(),
      String(input.year || "").trim(),
    ].filter(Boolean);
    const nameText = nameParts.join(style.detailSeparator).toUpperCase();
    const nameY = footerBottomY - style.NAME_Y_FROM_FOOTER_BOTTOM;

    const namePx = fitTextSize(
      ctx,
      nameText,
      maxWidth,
      style.nameSize,
      6,
      style.fontFamily,
    );
    ctx.font = `400 ${namePx}px ${style.fontFamily}, RockwellPoster`;
    drawSpacedText(ctx, nameText, centerX, nameY, style.letterSpacingName);

    // Detail line (line 2): Flower Name · Personality 1 · Personality 2
    const parts = [
      String(input.flowerName || "").trim(),
      String(input.personality1 || "").trim(),
      String(input.personality2 || "").trim(),
    ].filter(Boolean);
    const detailText = parts.join(style.detailSeparator).toUpperCase();
    const detailY = footerBottomY - style.DETAIL_Y_FROM_FOOTER_BOTTOM;
    ctx.font = `400 ${style.detailSize}px ${style.fontFamily}, RockwellPoster`;
    drawSpacedText(ctx, detailText, centerX, detailY, style.letterSpacingDetail);
  }

  // ============================================================
  // EXPORTS
  // ============================================================
  async function exportJpg({
    input,
    flowerImg,
    style,
    textureImg,
    longSide = 1200,
    quality = 0.9,
    sizeKey,
  }) {
    let w, h;
    if (sizeKey && SIZE_MM[sizeKey]) {
      const mm = SIZE_MM[sizeKey];
      const ratio = mm.h / mm.w;
      h = longSide;
      w = Math.round(longSide / ratio);
    } else {
      const ratio = DESIGN_H / DESIGN_W;
      h = longSide;
      w = Math.round(longSide / ratio);
    }
    const inputWithSize = {
      ...(input || {}),
      sizeKey: sizeKey || (input && input.sizeKey),
    };
    const { off, offCtx } = makeOffscreenCanvas(w, h);
    renderPoster(offCtx, flowerImg, inputWithSize, style, textureImg);
    return off.toDataURL("image/jpeg", quality);
  }

  async function exportPdf({
    input,
    flowerImg,
    style,
    textureImg,
    size,
    dpi = 300,
    jpegQuality = 0.95,
  }) {
    if (!window.jspdf || !window.jspdf.jsPDF) {
      throw new Error(
        "jsPDF not loaded. Add the jsPDF script tag on this page.",
      );
    }

    const mm = SIZE_MM[size];
    if (!mm) throw new Error("Unknown size for PDF: " + size);

    const pxW = mmToPx(mm.w, dpi);
    const pxH = mmToPx(mm.h, dpi);

    const { off, offCtx } = makeOffscreenCanvas(pxW, pxH);

    const inputWithSize = { ...(input || {}), sizeKey: size };
    renderPoster(offCtx, flowerImg, inputWithSize, style, textureImg);

    const imgData = off.toDataURL("image/jpeg", jpegQuality);

    const { jsPDF } = window.jspdf;
    const pdf = new jsPDF({
      orientation: mm.h >= mm.w ? "portrait" : "landscape",
      unit: "mm",
      format: [mm.w, mm.h],
    });

    pdf.addImage(imgData, "JPEG", 0, 0, mm.w, mm.h);
    return pdf;
  }

  function dataUrlToBlob(dataUrl) {
    const parts = dataUrl.split(",");
    const mime = parts[0].match(/:(.*?);/)[1];
    const bin = atob(parts[1]);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: mime });
  }

  function calcPrintLongSide(sizeKey, dpi) {
    dpi = dpi || 300;
    const mm = SIZE_MM[sizeKey] || SIZE_MM["50x70"];
    var longMm = Math.max(mm.w, mm.h);
    return Math.round((longMm / 25.4) * dpi);
  }

  // ============================================================
  // PUBLIC API
  // ============================================================
  return {
    DESIGN_W,
    DESIGN_H,
    SIZE_MM,
    DEFAULT_STYLE,

    setupPreviewCanvas,
    setupPreviewCanvasForSize,
    ensureFontLoaded,

    loadImageTo,
    renderPoster,

    upsertFlower,
    setDefaultFlowerKey,
    getFlower,

    exportJpg,
    exportPdf,

    downloadDataUrl,
    safeFilename,

    dataUrlToBlob,
    calcPrintLongSide,
  };
});
